import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiDollarSign, FiTrendingUp, FiTrendingDown, FiRefreshCw } from 'react-icons/fi';
import {
  fetchTransactions,
  getTransactionSummary,
  FinanceTransaction,
} from '../service/financeService';
import { useFinanceTransactions } from '../hooks/useFinanceTransactions';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${({ active }) =>
    active ? theme.colors.primary[500] : theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  background-color: ${({ active }) =>
    active ? theme.colors.primary[500] : theme.colors.white};
  color: ${({ active }) =>
    active ? theme.colors.white : theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${({ active }) =>
    active ? theme.typography.fontWeight.medium : theme.typography.fontWeight.normal};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ active }) =>
      active ? theme.colors.primary[600] : theme.colors.light.background.secondary};
    border-color: ${({ active }) =>
      active ? theme.colors.primary[600] : theme.colors.light.border.medium};
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const AmountCell = styled.div<{ type?: 'debit' | 'credit' }>`
  text-align: right;
  color: ${({ type }) => {
    if (type === 'debit') return theme.colors.accent.success; // Received (money in) - green
    if (type === 'credit') return theme.colors.accent.danger; // Expense (money out) - red
    return theme.colors.light.text.primary;
  }};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const BalanceCell = styled.div<{ balance?: number }>`
  text-align: right;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ balance }) => {
    if (!balance) return theme.colors.light.text.primary;
    return balance >= 0 ? theme.colors.accent.success : theme.colors.accent.danger;
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.secondary};
`;

const TransactionPage: React.FC = () => {
  const { user, companyData } = useAuth();
  const [timeFilter, setTimeFilter] = useState<'week' | 'month' | 'half-year' | 'year' | 'all'>('all');
  const [summary, setSummary] = useState({
    totalDebit: 0,
    totalCredit: 0,
    balance: 0,
    transactionCount: 0,
  });

  const expectedCompanyId = '';
  const companyId = companyData?.id || expectedCompanyId;

  // Use hook for all transactions with pagination
  const {
    transactions,
    loading,
    hasMore,
    loadMore,
    refresh,
    invalidateCache,
  } = useFinanceTransactions({
    companyId,
    timeFilter,
    pageSize: 20,
  });

  useEffect(() => {
    if (user) {
      loadSummary();
    }
  }, [user, companyData, timeFilter]);

  const loadSummary = async () => {
    try {
      const data = await getTransactionSummary(companyId, timeFilter);
      setSummary(data);
    } catch (error: any) {
      console.error('Error loading summary:', error);
    }
  };

  const handleRefresh = () => {
    refresh();
    loadSummary();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | any): string => {
    if (!date) return '—';
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Transactions</PageTitle>
        <Button onClick={handleRefresh} loading={loading}>
          <FiRefreshCw size={18} style={{ marginRight: theme.spacing[2] }} />
          Refresh
        </Button>
      </PageHeader>

      <FilterContainer>
        <FilterButton
          active={timeFilter === 'week'}
          onClick={() => setTimeFilter('week')}
        >
          This Week
        </FilterButton>
        <FilterButton
          active={timeFilter === 'month'}
          onClick={() => setTimeFilter('month')}
        >
          This Month
        </FilterButton>
        <FilterButton
          active={timeFilter === 'half-year'}
          onClick={() => setTimeFilter('half-year')}
        >
          Half Year
        </FilterButton>
        <FilterButton
          active={timeFilter === 'year'}
          onClick={() => setTimeFilter('year')}
        >
          This Year
        </FilterButton>
        <FilterButton
          active={timeFilter === 'all'}
          onClick={() => setTimeFilter('all')}
        >
          All Time
        </FilterButton>
      </FilterContainer>

      <StatsContainer>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.danger}>
              <FiTrendingDown size={20} />
              {formatCurrency(summary.totalDebit)}
            </StatValue>
            <StatLabel>Total Received</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.success}>
              <FiTrendingUp size={20} />
              {formatCurrency(summary.totalCredit)}
            </StatValue>
            <StatLabel>Total Expense</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={summary.balance >= 0 ? theme.colors.accent.success : theme.colors.accent.danger}>
              <FiDollarSign size={20} />
              {formatCurrency(summary.balance)}
            </StatValue>
            <StatLabel>Balance</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue>{summary.transactionCount}</StatValue>
            <StatLabel>Transactions</StatLabel>
          </CardBody>
        </StatCard>
      </StatsContainer>

      <Card>
        <CardBody>
          {loading ? (
            <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
              Loading transactions...
            </div>
          ) : transactions.length > 0 ? (
            <>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Date</Table.HeaderCell>
                    <Table.HeaderCell>Particulars</Table.HeaderCell>
                    <Table.HeaderCell>Ref</Table.HeaderCell>
                    <Table.HeaderCell style={{ textAlign: 'right' }}>Received</Table.HeaderCell>
                    <Table.HeaderCell style={{ textAlign: 'right' }}>Expense</Table.HeaderCell>
                    <Table.HeaderCell style={{ textAlign: 'right' }}>Balance</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {transactions.map((transaction) => (
                    <Table.Row key={transaction.id}>
                      <Table.Cell>{formatDate(transaction.date)}</Table.Cell>
                      <Table.Cell>{transaction.particulars || '—'}</Table.Cell>
                      <Table.Cell>{transaction.ref || '—'}</Table.Cell>
                      <Table.Cell style={{ textAlign: 'right' }}>
                        <AmountCell type="debit">
                          {transaction.debit > 0 ? formatCurrency(transaction.debit) : '—'}
                        </AmountCell>
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: 'right' }}>
                        <AmountCell type="credit">
                          {transaction.credit > 0 ? formatCurrency(transaction.credit) : '—'}
                        </AmountCell>
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: 'right' }}>
                        <BalanceCell balance={transaction.balance}>
                          {formatCurrency(transaction.balance)}
                        </BalanceCell>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              {hasMore && (
                <div style={{ marginTop: theme.spacing[4], textAlign: 'center' }}>
                  <Button
                    onClick={loadMore}
                    loading={loading}
                    variant="secondary"
                  >
                    Load More
                  </Button>
                </div>
              )}
            </>
          ) : (
            <EmptyState>
              <FiDollarSign size={48} style={{ marginBottom: theme.spacing[2], opacity: 0.5 }} />
              <p>No transactions found for the selected period.</p>
            </EmptyState>
          )}
        </CardBody>
      </Card>
    </DashboardLayout>
  );
};

export default TransactionPage;
